<template>
  <!-- Simple overlay for nested iframe scenarios (bypasses HeadlessUI scroll lock) -->
  <div v-if="needsOffsetWorkaround && isSimpleModalVisible" class="simple-modal-overlay">
    <div class="simple-modal-backdrop" />
    <div class="simple-modal-panel" :style="{ marginTop: offset + 'px' }">
      <UCard>
        <template #header>
          <div class="p-2">
            <h3 class="text-lg font-semibold text-primary">Add something extra?</h3>
            <p class="text-sm text-secondary">Choose one or more items to add to your booking</p>
          </div>
        </template>
        <div v-if="associations.length > 0" class="space-y-2">
          <BookingElementsAddOn
            v-for="association in associations"
            :key="association.id"
            :association="association"
            :selected-employees="association.employee"
          />
        </div>

        <template #footer>
          <UButton block @click="onSuccess">
            Next
          </UButton>
        </template>
      </UCard>
    </div>
  </div>

  <!-- Standard UModal for normal scenarios -->
  <UModal v-else :ui="modalUi">
    <UCard>
      <template #header>
        <div class="p-2">
          <h3 class="text-lg font-semibold text-primary">Add something extra?</h3>
          <p class="text-sm text-secondary">Choose one or more items to add to your booking</p>
        </div>
      </template>
      <div v-if="associations.length > 0" class="space-y-2">
        <BookingElementsAddOn
          v-for="association in associations"
          :key="association.id"
          :association="association"
          :selected-employees="association.employee"
        />
      </div>

      <template #footer>
        <UButton block @click="onSuccess">
          Next
        </UButton>
      </template>
    </UCard>
  </UModal>
</template>

<script lang="ts" setup>
import type { ServiceAssociation, Employee } from '@@/types/api';
import Utils from '@/utils'

type AssociationWithEmployees = ServiceAssociation & { employee: Employee[] }

interface Props {
  anchorTop?: number
}

const props = withDefaults(defineProps<Props>(), {
  anchorTop: 0
})

// Only apply offset for Safari in nested iframes (Wix-like scenario)
const needsOffsetWorkaround = computed(() => {
  return Utils.browser.isSafari() && Utils.browser.inNestedIframe()
})

// Local visibility state for the simple overlay (since it doesn't use UModal's state)
const isSimpleModalVisible = ref(true)

const offset = computed(() => {
  if (!needsOffsetWorkaround.value) return 0
  return props.anchorTop - 300 || 100
})

const layoutStore = useLayoutStore()
const modal = useModal()

const emit = defineEmits(['success'])

function onSuccess() {
  if (needsOffsetWorkaround.value) {
    isSimpleModalVisible.value = false
  } else {
    modal.close()
  }
  emit('success')
}

const BookingStore = useBookingStore()

const selectedServices = computed(() => {
  return BookingStore.selectedServices
})

const isWidgetLayout = computed(() => {
  return layoutStore.layout === 'widget'
})

// In widget mode, position modal at top
const modalUi = computed(() => {
  if (isWidgetLayout.value) {
    return {
      container: 'flex min-h-full items-start sm:items-start justify-center text-center'
    }
  }
  return {}
})

// Filter the list of associations from the services and remove duplicates
// Keep the association with the highest discount

const associations = ref<AssociationWithEmployees[]>([])

onMounted(() => {
  // In widget mode (without workaround), scroll to top so the modal is visible
  if (isWidgetLayout.value && !needsOffsetWorkaround.value) {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    // Also tell the parent window to scroll the iframe into view
    if (Utils.browser.inIframe() && window.parent) {
      window.postRobot?.send(window.parent, 'bookapp.event.page-changed', window.location.pathname)
    }
  }

  const items = Object.values(selectedServices.value)
    .reduce((acc, { service }) => {
      if (!service.associations) {
        return acc
      }

      for (const association of service.associations) {
        // Skip the association if the target is already in the props.services list
        if (selectedServices.value[association.target.id] && selectedServices.value[association.target.id]?.selected) {
          continue
        }

        // Skip the association if it already exists in the accumulator
        if (acc[association.target.id]) {
          if (acc[association.target.id].discountAmount > association.discountAmount) {

            // Push the employee of the parent service to the accumulator
            acc[association.target.id].employee.push(service.employee)

            // Continue to the next association
            continue
          }
        }

        acc[association.target.id] = {
          ...association,
          employee: [service.employee]
        }
      }

      return acc
    }, {})


  associations.value = Object.values(items)
})
</script>

<style scoped>
/* Simple modal overlay - no scroll lock, just visual overlay */
.simple-modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-height: 100%;
  display: flex;
  justify-content: center;
  padding: 1rem;
  pointer-events: none;
  z-index: 50;
}

.simple-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  touch-action: pan-y;
}

.simple-modal-panel {
  position: relative;
  pointer-events: auto;
  width: 100%;
  max-width: 28rem;
}
</style>
